import React, { useEffect, useState } from 'react';
import QuickCustomerForm from '../quote/QuickCustomerForm';
import QuickVehicleForm from '../quote/QuickVehicleForm';
import QuickServiceForm from '../quote/QuickServiceForm';
import QuickPartSelector from '../quote/QuickPartSelector';
import PartForm from '../quote/PartForm';

const InvoiceForm = ({ invoice, customers, vehicles, services, parts, settings, quotes = [], onSubmit, onCancel, loading, onCreateCustomer, onCreateVehicle, onCreateService, onCreatePart, defaultCustomerId, defaultQuoteId, initialInstallments = [] }) => {
  const [formData, setFormData] = useState({
    invoice_number: '',
    customer_id: '',
    project_id: '',
    quote_id: '',
    status: 'draft',
    due_date: '',
    payment_status: 'pending',
    payment_date: '',
    payment_method: '',
    notes: '',
    items: [],
    discount_type: '',
    discount_value: '',
    discount_label: '',
    tax_enabled: true,
    installments_count: 3,
    first_due_date: '',
  });

  const [errors, setErrors] = useState({});
  const [availableVehicles, setAvailableVehicles] = useState([]);
  const [availableQuotes, setAvailableQuotes] = useState([]);
  const [showQuickCustomerForm, setShowQuickCustomerForm] = useState(false);
  const [showQuickVehicleForm, setShowQuickVehicleForm] = useState(false);
  const [showQuickServiceForm, setShowQuickServiceForm] = useState(false);
  const [showPartSelector, setShowPartSelector] = useState(false);
  const [showQuickPartForm, setShowQuickPartForm] = useState(false);
  const [creatingCustomer, setCreatingCustomer] = useState(false);
  const [creatingVehicle, setCreatingVehicle] = useState(false);
  const [creatingService, setCreatingService] = useState(false);
  const [creatingPart, setCreatingPart] = useState(false);
  // Recherche inline pour le select Service (par ligne)
  const [serviceSearch, setServiceSearch] = useState({}); // { [itemId]: { active: bool, term: string } }
  // Éditeur de ligne (popup)
  const [showItemEditor, setShowItemEditor] = useState(false);
  const [activeItemId, setActiveItemId] = useState(null);
  const [editorIsNewItem, setEditorIsNewItem] = useState(false);

  const [installments, setInstallments] = useState(initialInstallments || []);
  const [genPlan, setGenPlan] = useState({
    count: 3,
    frequency: 'monthly', // monthly, weekly, custom
    start_date: '',
    deposit_enabled: false,
    deposit_type: 'percent', // percent, fixed
    deposit_value: 30,
  });

  useEffect(() => {
    if (initialInstallments) setInstallments(initialInstallments);
  }, [initialInstallments]);

  // Sync genPlan start_date with form due_date if empty
  useEffect(() => {
    if (formData.first_due_date && !genPlan.start_date) setGenPlan(prev => ({ ...prev, start_date: formData.first_due_date }));
    else if (formData.due_date && !genPlan.start_date) setGenPlan(prev => ({ ...prev, start_date: formData.due_date }));
  }, [formData.first_due_date, formData.due_date]);

  // Generate Installments Logic
  const generateInstallments = () => {
    const n = Math.max(1, Number(genPlan.count || 1));
    const total = Number(totals.total || 0);
    const result = [];
    let remaining = total;
    let nextDate = new Date(genPlan.start_date || new Date());

    // Deposit logic
    let firstAmount = 0;
    if (genPlan.deposit_enabled && genPlan.deposit_value > 0) {
      if (genPlan.deposit_type === 'percent') {
        firstAmount = total * (genPlan.deposit_value / 100);
      } else {
        firstAmount = Number(genPlan.deposit_value);
      }
      firstAmount = Math.min(firstAmount, total);
    } else {
      firstAmount = total / n;
    }

    // First installment (Deposit or Equal share)
    result.push({
      label: genPlan.deposit_enabled ? 'Acompte' : `Échéance 1/${n}`,
      amount_due: firstAmount,
      due_date: (!isNaN(nextDate.getTime())) ? nextDate.toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      status: 'pending'
    });
    remaining -= firstAmount;

    // Remaining installments
    // Fix: Strict respect of total count N. Remaining = N - 1.
    const remainingCount = Math.max(0, n - 1);

    if (remainingCount > 0) {
      const perRest = remaining / remainingCount;
      for (let i = 0; i < remainingCount; i++) {
        // Next date
        if (genPlan.frequency === 'monthly') nextDate.setMonth(nextDate.getMonth() + 1);
        else if (genPlan.frequency === 'weekly') nextDate.setDate(nextDate.getDate() + 7);

        result.push({
          label: genPlan.deposit_enabled ? `Échéance ${i + 1}/${remainingCount}` : `Échéance ${i + 2}/${n}`,
          amount_due: perRest,
          due_date: (!isNaN(nextDate.getTime())) ? nextDate.toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
          status: 'pending'
        });
      }
    }

    // update state
    setInstallments(result);
    // sync count and first due date
    setFormData(prev => ({
      ...prev,
      installments_count: n,
      first_due_date: genPlan.start_date
    }));
  };

  // Toggle for split payments (UI only)
  const [splitPaymentEnabled, setSplitPaymentEnabled] = useState(false);

  const statuses = [
    { value: 'draft', label: 'Brouillon' },
    { value: 'sent', label: 'Envoyée' },
    { value: 'paid', label: 'Payée' },
    { value: 'overdue', label: 'En retard' },
    { value: 'cancelled', label: 'Annulée' },
  ];

  const paymentStatuses = [
    { value: 'pending', label: 'En attente' },
    { value: 'partial', label: 'Partiel' },
    { value: 'paid', label: 'Payé' },
    { value: 'overdue', label: 'En retard' },
  ];

  const paymentMethods = [
    { value: 'cash', label: 'Espèces' },
    { value: 'check', label: 'Chèque' },
    { value: 'card', label: 'Carte bancaire' },
    { value: 'transfer', label: 'Virement' },
    { value: 'other', label: 'Autre' },
  ];

  useEffect(() => {
    if (invoice) {
      const dueDate = (() => {
        if (!invoice.due_date) return '';
        const d = new Date(invoice.due_date);
        return isNaN(d.getTime()) ? '' : d.toISOString().split('T')[0];
      })();
      const paymentDate = (() => {
        if (!invoice.payment_date) return '';
        const d = new Date(invoice.payment_date);
        return isNaN(d.getTime()) ? '' : d.toISOString().split('T')[0];
      })();
      setFormData({
        invoice_number: invoice.invoice_number || '',
        customer_id: invoice.customer_id || '',
        project_id: invoice.project_id || invoice.vehicle_id || '',
        quote_id: invoice.quote_id || '',
        status: invoice.status || 'draft',
        due_date: dueDate,
        payment_status: invoice.payment_status || 'pending',
        payment_date: paymentDate,
        payment_method: invoice.payment_method || '',
        notes: invoice.notes || '',
        items: invoice.items || [],
        discount_type: invoice.discount_type || '',
        discount_value: Number(invoice.discount_value || 0),
        discount_label: invoice.discount_label || '',
        tax_enabled: typeof invoice.tax_enabled === 'boolean' ? invoice.tax_enabled : true,
        installments_count: Number(invoice.installments_count || 3),
        first_due_date: (() => {
          if (!invoice.first_due_date) return (dueDate || '');
          const d = new Date(invoice.first_due_date);
          return isNaN(d.getTime()) ? (dueDate || '') : d.toISOString().split('T')[0];
        })(),
      });
      try { setSplitPaymentEnabled(Number(invoice.installments_count || 0) > 1); } catch { }
      if (invoice.customer_id) {
        updateAvailableVehicles(invoice.customer_id);
        updateAvailableQuotes(invoice.customer_id);
      }
    } else {
      if (settings) {
        const nextNumber = `${settings.invoice_prefix || 'FACT'}-${String(settings.invoice_counter || 1).padStart(4, '0')}`;
        setFormData(prev => ({ ...prev, invoice_number: nextNumber }));
      }
      const terms = Number(settings?.invoice_payment_terms) || 30;
      const due = new Date();
      due.setDate(due.getDate() + terms);
      const dueStr = (!isNaN(due.getTime())) ? due.toISOString().split('T')[0] : new Date().toISOString().split('T')[0];
      setFormData(prev => ({ ...prev, due_date: dueStr, first_due_date: dueStr }));
      setSplitPaymentEnabled(false);
      if (defaultCustomerId) {
        setFormData(prev => ({ ...prev, customer_id: defaultCustomerId }));
        updateAvailableVehicles(defaultCustomerId);
        updateAvailableQuotes(defaultCustomerId);
      }
      if (defaultQuoteId) {
        const q = quotes.find(q => q.id === defaultQuoteId);
        if (q) {
          setFormData(prev => ({
            ...prev,
            customer_id: q.customer_id || prev.customer_id,
            project_id: q.vehicle_id || q.project_id || prev.project_id,
            quote_id: q.id,
            items: (q.items || []).map(item => ({ ...item, id: Date.now() + Math.random() })),
          }));
          updateAvailableVehicles(q.customer_id);
          updateAvailableQuotes(q.customer_id);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoice, customers, settings, quotes, defaultCustomerId, defaultQuoteId]);

  useEffect(() => { calculateTotals(); }, [formData.items, formData.discount_type, formData.discount_value, formData.tax_enabled, settings]);

  const updateAvailableVehicles = (customerId) => {
    try {
      const filtered = vehicles.filter(v => {
        const link = v.client || v.customer_id || v.customer;
        return link === customerId;
      });
      setAvailableVehicles(filtered);
    } catch {
      setAvailableVehicles([]);
    }
  };

  // Keep available projects in sync when vehicles list changes
  useEffect(() => {
    if (formData.customer_id) updateAvailableVehicles(formData.customer_id);
  }, [vehicles]);
  const updateAvailableQuotes = (customerId) => {
    setAvailableQuotes(quotes.filter(q => q.customer_id === customerId && q.status === 'accepted'));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value === '' ? '' : value }));
    if (name === 'customer_id') {
      updateAvailableVehicles(value);
      updateAvailableQuotes(value);
      setFormData(prev => ({ ...prev, project_id: '', quote_id: '', items: [] }));
    }
    if (name === 'quote_id' && value) {
      const q = quotes.find(q => q.id === value);
      if (q) {
        setFormData(prev => ({ ...prev, project_id: q.project_id || q.vehicle_id, items: (q.items || []).map(item => ({ ...item, id: Date.now() + Math.random() })) }));
      }
    }
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const addItem = () => {
    const id = Date.now();
    const newItem = { id, service_id: '', description: '', quantity: 1, unit_price: '', tax_rate: settings?.default_tax_rate || 20, total: 0 };
    setFormData(prev => ({ ...prev, items: [...prev.items, newItem] }));
    return id;
  };
  const addAndEditItem = () => {
    const id = addItem();
    setActiveItemId(id);
    setEditorIsNewItem(true);
    setShowItemEditor(true);
  };
  const updateItem = (itemId, field, value) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map(item => {
        if (item.id === itemId) {
          const updated = { ...item, [field]: value };
          if (field === 'service_id' && value) {
            const s = services.find(s => s.id === value);
            if (s) { updated.description = s.name; updated.unit_price = s.base_price; }
          }

          // Always recalculate total to ensure consistency (especially when service_id changes unit_price)
          const q = updated.quantity === '' ? 0 : Number(updated.quantity);
          const p = updated.unit_price === '' ? 0 : Number(updated.unit_price);
          updated.total = q * p;

          return updated;
        }
        return item;
      })
    }));
  };
  const removeItem = (itemId) => setFormData(prev => ({ ...prev, items: prev.items.filter(i => i.id !== itemId) }));

  const [totals, setTotals] = useState({ subtotal: 0, discount_total: 0, tax_amount: 0, total: 0 });
  const calculateTotals = () => {
    const subtotal = formData.items.reduce((sum, it) => sum + (Number(it.quantity || 0) * Number(it.unit_price || 0)), 0);
    const val = Number(formData.discount_value || 0);
    let discount = 0; if (formData.discount_type === 'percent') discount = subtotal * (val / 100); else if (formData.discount_type === 'fixed') discount = val; discount = Math.max(0, Math.min(discount, subtotal));
    const taxRate = (settings?.default_tax_rate || 20) / 100; const taxBase = subtotal - discount; const taxAmount = formData.tax_enabled ? taxBase * taxRate : 0; const total = taxBase + taxAmount;
    setTotals({ subtotal, discount_total: discount, tax_amount: taxAmount, total });
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.customer_id) newErrors.customer_id = 'Veuillez sélectionner un client';
    if (!formData.project_id) newErrors.project_id = 'Veuillez sélectionner un projet';
    if (!formData.due_date) newErrors.due_date = "La date d'échéance est requise";
    if (formData.items.length === 0) newErrors.items = 'Ajoutez au moins une prestation';
    const badItem = formData.items.some(i => !i.description || i.quantity <= 0 || i.unit_price < 0);
    if (badItem) newErrors.items = 'Vérifiez les informations des prestations';
    if (formData.payment_status === 'paid' && !formData.payment_date) newErrors.payment_date = 'Date de paiement requise';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    const payload = { ...formData, ...totals, tax_rate: settings?.default_tax_rate || 20 };
    if (!splitPaymentEnabled) {
      payload.installments_count = 1;
      if (!payload.first_due_date) delete payload.first_due_date;
      payload.installments = [];
    } else {
      // ensure sensible defaults when enabled
      payload.installments_count = Math.max(2, Number(payload.installments_count || 2));
      if (!payload.first_due_date) {
        const d = payload.due_date ? new Date(payload.due_date) : new Date();
        payload.first_due_date = (!isNaN(d.getTime())) ? d.toISOString().split('T')[0] : new Date().toISOString().split('T')[0];
      }
      // Attach installments
      payload.installments = installments;
    }
    onSubmit(payload);
  };
  const handleAddPart = (partItem) => { const newItem = { id: Date.now(), ...partItem }; setFormData(prev => ({ ...prev, items: [...prev.items, newItem] })); setShowPartSelector(false); };

  const noopWrap = async (fn, data) => { try { return await fn(data); } catch (e) { console.error(e); } };

  /* Drag & Drop State */
  const [draggedItemIndex, setDraggedItemIndex] = useState(null);

  const handleDragStart = (index) => {
    setDraggedItemIndex(index);
  };

  const handleDragOver = (e) => {
    e.preventDefault(); // Necessary to allow dropping
  };

  const handleDrop = (index) => {
    if (draggedItemIndex === null || draggedItemIndex === index) return;

    const newItems = [...formData.items];
    const itemToMove = newItems[draggedItemIndex];
    newItems.splice(draggedItemIndex, 1);
    newItems.splice(index, 0, itemToMove);

    setFormData(prev => ({ ...prev, items: newItems }));
    setDraggedItemIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedItemIndex(null);
  };

  return (
    <div className="invoice-form-overlay">
      <div className="invoice-form-container">
        {/* ... (header and inputs remain same) ... */}
        <div className="invoice-form-header">
          <h2>{invoice ? 'Modifier la facture' : 'Nouvelle facture'}</h2>
          <button className="close-btn" onClick={onCancel}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" /></svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="invoice-form">
          <div className="form-section">
            <h3>Informations générales</h3>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="invoice_number">Numéro de facture</label>
                <input type="text" id="invoice_number" name="invoice_number" value={formData.invoice_number} onChange={handleChange} readOnly className="readonly" />
              </div>
              <div className="form-group">
                <label htmlFor="status">Statut</label>
                <select id="status" name="status" value={formData.status} onChange={handleChange}>
                  {statuses.map(s => (<option key={s.value} value={s.value}>{s.label}</option>))}
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="customer_id">Client *</label>
                <div className="select-with-add">
                  <select id="customer_id" name="customer_id" value={formData.customer_id} onChange={handleChange} className={errors.customer_id ? 'error' : ''}>
                    <option value="">Sélectionner un client...</option>
                    {customers.map(c => (<option key={c.id} value={c.id}>{c.name} {c.company && `(${c.company})`}</option>))}
                  </select>
                  <button type="button" className="add-btn" onClick={() => setShowQuickCustomerForm(true)} title="Ajouter un nouveau client">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" /></svg>
                  </button>
                </div>
                {errors.customer_id && <span className="error-text">{errors.customer_id}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="project_id">Projet *</label>
                <div className="select-with-add">
                  <select id="project_id" name="project_id" value={formData.project_id} onChange={handleChange} disabled={!formData.customer_id} className={errors.project_id ? 'error' : ''}>
                    <option value="">Sélectionner un projet...</option>
                    {availableVehicles.map(p => (
                      <option key={p.id} value={p.id}>
                        {(p.name || p.brand || 'Projet')}{p.internal_code ? ` — ${p.internal_code}` : ''}
                      </option>
                    ))}
                  </select>
                  <button type="button" className="add-btn" onClick={() => setShowQuickVehicleForm(true)} disabled={!formData.customer_id} title="Ajouter un nouveau projet">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" /></svg>
                  </button>
                </div>
                {errors.project_id && <span className="error-text">{errors.project_id}</span>}
                {!formData.customer_id && (<span className="info-text">Sélectionnez d'abord un client pour ajouter un projet</span>)}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="quote_id">Devis associé (optionnel)</label>
                <select id="quote_id" name="quote_id" value={formData.quote_id} onChange={handleChange} disabled={!formData.customer_id}>
                  <option value="">Aucun devis / Facture libre</option>
                  {availableQuotes.map(q => (<option key={q.id} value={q.id}>{q.quote_number} - {(q.total || 0).toFixed(2)}€ ({q.status})</option>))}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="due_date">Date d'échéance *</label>
                <input type="date" id="due_date" name="due_date" value={formData.due_date} onChange={handleChange} className={errors.due_date ? 'error' : ''} />
                {errors.due_date && <span className="error-text">{errors.due_date}</span>}
              </div>
            </div>
          </div>

          <div className="form-section">
            <div className="section-header">
              <h3>Prestations</h3>
              <div className="section-actions">
                <button type="button" onClick={addAndEditItem} className="btn-add-item"><svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" /></svg>Ajouter une prestation</button>
                <button type="button" className="btn-create-service" onClick={() => setShowQuickServiceForm(true)} title="Créer une nouvelle prestation"><svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5 11h-4v4h-2v-4H7v-2h4V7h2v4h4v2z" /></svg>Nouvelle prestation</button>
                <button type="button" className="btn-add-part" onClick={() => setShowPartSelector(true)} title="Ajouter un produit du catalogue"><svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" /></svg>Ajouter un produit</button>
                <button type="button" className="btn-create-part" onClick={() => setShowQuickPartForm(true)} title="Créer un nouveau produit"><svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5 11h-4v4h-2v-4H7v-2h4V7h2v4h4v2z" /></svg>Nouveau produit</button>
              </div>
            </div>

            {formData.items.length > 0 ? (
              <div className="items-table">
                <div className="table-header" style={{ gridTemplateColumns: '40px 200px 1fr 80px 120px 100px 60px' }}>
                  <div>#</div><div>Service</div><div>Description</div><div>Qté</div><div>Prix unitaire</div><div>Total</div><div>Actions</div>
                </div>
                {formData.items.map((item, index) => {
                  const term = serviceSearch[item.id]?.term || '';
                  const filteredServices = services.filter(s => s.active && (term ? (s.name || '').toLowerCase().includes(term.toLowerCase()) : true));
                  const active = !!serviceSearch[item.id]?.active;
                  return (
                    <div
                      key={item.id}
                      className="table-row"
                      style={{
                        gridTemplateColumns: '40px 200px 1fr 80px 120px 100px 60px',
                        background: draggedItemIndex === index ? 'var(--admin-panel-2)' : 'transparent',
                        opacity: draggedItemIndex === index ? 0.5 : 1,
                        cursor: 'grab'
                      }}
                      draggable
                      onDragStart={() => handleDragStart(index)}
                      onDragOver={handleDragOver}
                      onDrop={() => handleDrop(index)}
                      onDragEnd={handleDragEnd}
                    >
                      <div className="item-order" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'grab' }} title="Glisser pour réorganiser">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.5 }}>
                          <circle cx="9" cy="5" r="1" /><circle cx="9" cy="12" r="1" /><circle cx="9" cy="19" r="1" />
                          <circle cx="15" cy="5" r="1" /><circle cx="15" cy="12" r="1" /><circle cx="15" cy="19" r="1" />
                        </svg>
                      </div>
                      <div className="item-service">
                        {active && (
                          <input
                            type="text"
                            className="service-search-overlay"
                            placeholder="Rechercher une prestation..."
                            autoFocus
                            value={term}
                            onChange={(e) => setServiceSearch(prev => ({ ...prev, [item.id]: { active: true, term: e.target.value } }))}
                            onKeyDown={(e) => {
                              if (e.key === 'Escape') { setServiceSearch(prev => ({ ...prev, [item.id]: { active: false, term: '' } })); }
                              if (e.key === 'Enter') {
                                const m = filteredServices[0];
                                if (m) { updateItem(item.id, 'service_id', m.id); setServiceSearch(prev => ({ ...prev, [item.id]: { active: false, term: '' } })); }
                              }
                            }}
                          />
                        )}
                        <select
                          value={item.service_id}
                          onChange={(e) => { updateItem(item.id, 'service_id', e.target.value); setServiceSearch(prev => ({ ...prev, [item.id]: { active: false, term: '' } })); }}
                          onFocus={() => setServiceSearch(prev => ({ ...prev, [item.id]: { active: true, term: prev[item.id]?.term || '' } }))}
                        >
                          <option value="">Service personnalisé</option>
                          {filteredServices.map(s => (<option key={s.id} value={s.id}>{s.name} ({s.base_price}€)</option>))}
                        </select>
                      </div>
                      <div className="item-description"><input type="text" value={item.description} onChange={(e) => updateItem(item.id, 'description', e.target.value)} placeholder="Description de la prestation" /></div>
                      <div className="item-quantity">
                        <input
                          type="number"
                          value={item.quantity}
                          onChange={(e) => updateItem(item.id, 'quantity', e.target.value)}
                          onFocus={(e) => e.target.select()}
                          onWheel={(e) => e.currentTarget.blur()}
                          onKeyDown={(e) => ["ArrowUp", "ArrowDown"].includes(e.key) && e.preventDefault()}
                          min="0"
                          step="1"
                          inputMode="numeric"
                          className="no-spin"
                        />
                      </div>
                      <div className="item-price">
                        <input
                          type="number"
                          value={item.unit_price}
                          onChange={(e) => updateItem(item.id, 'unit_price', e.target.value)}
                          onFocus={(e) => e.target.select()}
                          onWheel={(e) => e.currentTarget.blur()}
                          onKeyDown={(e) => ["ArrowUp", "ArrowDown"].includes(e.key) && e.preventDefault()}
                          min="0"
                          step="0.01"
                          inputMode="decimal"
                          className="no-spin"
                        />
                      </div>
                      <div className="item-total">{(Number(item.quantity || 0) * Number(item.unit_price || 0)).toFixed(2)}€</div>
                      <div className="item-actions"><button type="button" onClick={() => removeItem(item.id)} className="btn-remove-item" title="Supprimer"><svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" /></svg></button></div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="empty-items"><p>Aucune prestation ajoutée</p><button type="button" onClick={addItem} className="btn-primary">Ajouter la première prestation</button></div>
            )}
            {errors.items && <span className="error-text">{errors.items}</span>}
          </div>

          {formData.items.length > 0 && (
            <div className="form-section">
              <h3>Récapitulatif</h3>
              <div className="form-row" style={{ marginBottom: 8 }}>
                <div className="form-group"><label>Type de remise</label><select name="discount_type" value={formData.discount_type} onChange={handleChange}><option value="">Aucune</option><option value="percent">Pourcentage</option><option value="fixed">Montant fixe</option></select></div>
                <div className="form-group">
                  <label>Valeur</label>
                  <input
                    type="number"
                    name="discount_value"
                    step="0.01"
                    min="0"
                    value={formData.discount_value}
                    onChange={handleChange}
                    onWheel={(e) => e.currentTarget.blur()}
                    onKeyDown={(e) => ["ArrowUp", "ArrowDown"].includes(e.key) && e.preventDefault()}
                    inputMode="decimal"
                    placeholder={formData.discount_type === 'percent' ? 'Ex: 10 (%)' : 'Ex: 50 (€)'}
                    disabled={!formData.discount_type}
                    className="no-spin"
                  />
                </div>
                <div className="form-group"><label>Intitulé (optionnel)</label><input type="text" name="discount_label" value={formData.discount_label} onChange={handleChange} placeholder="Ex: Promo" disabled={!formData.discount_type} /></div>
                <div className="form-group" style={{ alignSelf: 'end' }}><label style={{ display: 'block' }}>TVA applicable</label><input type="checkbox" checked={!!formData.tax_enabled} onChange={(e) => setFormData(prev => ({ ...prev, tax_enabled: e.target.checked }))} /></div>
              </div>
              <div className="totals-section">
                <div className="totals-row"><span>Sous-total HT :</span><span>{totals.subtotal.toFixed(2)}€</span></div>
                {totals.discount_total > 0 && (<div className="totals-row"><span>Remise{formData.discount_type === 'percent' ? ` (${Number(formData.discount_value || 0)}%)` : ''}{formData.discount_label ? ` - ${formData.discount_label}` : ''} :</span><span>-{totals.discount_total.toFixed(2)}€</span></div>)}
                {formData.tax_enabled ? (<div className="totals-row"><span>TVA ({settings?.default_tax_rate || 20}%) :</span><span>{totals.tax_amount.toFixed(2)}€</span></div>) : (<div className="totals-row"><span>TVA non applicable</span><span>-</span></div>)}
                <div className="totals-row total"><span>Total TTC :</span><span>{totals.total.toFixed(2)}€</span></div>
              </div>
            </div>
          )}

          <div className="form-section">
            <h3>Notes</h3>
            <div className="form-group"><textarea id="notes" name="notes" value={formData.notes} onChange={handleChange} placeholder="Notes de facture, conditions de paiement..." rows="4" /></div>
          </div>

          <div className="form-section">
            <h3>Informations de paiement</h3>
            <div className="form-row">
              <div className="form-group"><label htmlFor="payment_status">Statut de paiement</label><select id="payment_status" name="payment_status" value={formData.payment_status} onChange={handleChange}>{paymentStatuses.map(ps => (<option key={ps.value} value={ps.value}>{ps.label}</option>))}</select></div>
              <div className="form-group"><label htmlFor="payment_method">Mode de paiement</label><select id="payment_method" name="payment_method" value={formData.payment_method} onChange={handleChange}><option value="">Sélectionner...</option>{paymentMethods.map(pm => (<option key={pm.value} value={pm.value}>{pm.label}</option>))}</select></div>
            </div>
            {formData.payment_status === 'paid' && (<div className="form-group"><label htmlFor="payment_date">Date de paiement</label><input type="date" id="payment_date" name="payment_date" value={formData.payment_date} onChange={handleChange} className={errors.payment_date ? 'error' : ''} />{errors.payment_date && <span className="error-text">{errors.payment_date}</span>}</div>)}

            <div className="form-row">
              <div className="form-group" style={{ alignSelf: 'end' }}>
                <label style={{ display: 'block' }}>Paiement en plusieurs fois</label>
                <input type="checkbox" checked={!!splitPaymentEnabled} onChange={(e) => setSplitPaymentEnabled(!!e.target.checked)} />
              </div>
            </div>
            {splitPaymentEnabled && (
              <div className="installments-generator" style={{ background: 'var(--admin-panel-2)', padding: 16, borderRadius: 8, marginTop: 16, border: '1px solid var(--admin-border)' }}>
                <h4 style={{ margin: '0 0 16px', fontSize: '0.95rem', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M19 3h-4.18C14.4 1.84 13.3 1 12 1c-1.3 0-2.4.84-2.82 2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 0c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm2 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z" /></svg>
                  Configuration de l'échéancier
                </h4>

                <div className="form-row">
                  <div className="form-group">
                    <label>Nombre total d'échéances</label>
                    <input
                      type="number"
                      min="1"
                      max="24"
                      value={genPlan.count || ''}
                      onChange={e => setGenPlan({ ...genPlan, count: e.target.value === '' ? '' : parseInt(e.target.value) })}
                      onWheel={(e) => e.currentTarget.blur()}
                      onKeyDown={(e) => ["ArrowUp", "ArrowDown"].includes(e.key) && e.preventDefault()}
                      className="no-spin"
                    />
                  </div>
                  <div className="form-group">
                    <label>Fréquence</label>
                    <select value={genPlan.frequency} onChange={e => setGenPlan({ ...genPlan, frequency: e.target.value })}>
                      <option value="monthly">Mensuelle</option>
                      <option value="weekly">Hebdomadaire</option>
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Première échéance le</label>
                    <input type="date" value={genPlan.start_date} onChange={e => setGenPlan({ ...genPlan, start_date: e.target.value })} />
                  </div>
                </div>

                <div className="form-row" style={{ alignItems: 'center' }}>
                  <div className="form-group">
                    <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                      <input type="checkbox" checked={genPlan.deposit_enabled} onChange={e => setGenPlan({ ...genPlan, deposit_enabled: e.target.checked })} />
                      Inclure un acompte initial
                    </label>
                  </div>
                </div>

                {genPlan.deposit_enabled && (
                  <div className="form-row">
                    <div className="form-group">
                      <label>Type d'acompte</label>
                      <select value={genPlan.deposit_type} onChange={e => setGenPlan({ ...genPlan, deposit_type: e.target.value })}>
                        <option value="percent">Pourcentage (%)</option>
                        <option value="fixed">Montant fixe (€)</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Valeur</label>
                      <input
                        type="number"
                        value={genPlan.deposit_value || ''}
                        onChange={e => setGenPlan({ ...genPlan, deposit_value: e.target.value === '' ? '' : parseFloat(e.target.value) })}
                        onWheel={(e) => e.currentTarget.blur()}
                        onKeyDown={(e) => ["ArrowUp", "ArrowDown"].includes(e.key) && e.preventDefault()}
                        className="no-spin"
                      />
                    </div>
                  </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
                  <button type="button" className="btn-primary" onClick={generateInstallments}>
                    Générer l'échéancier
                  </button>
                </div>

                {installments.length > 0 && (
                  <div className="installments-preview" style={{ marginTop: 24, borderTop: '1px solid var(--admin-border)', paddingTop: 16 }}>
                    <h5 style={{ margin: '0 0 12px', fontSize: '0.9rem' }}>Aperçu de l'échéancier</h5>
                    <div className="preview-table" style={{ background: 'var(--admin-panel)', borderRadius: 6, overflow: 'hidden', border: '1px solid var(--admin-border)' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '40px 1fr 120px 100px', background: 'var(--admin-panel-2)', padding: '8px 12px', fontSize: '0.85rem', fontWeight: 600 }}>
                        <div>#</div>
                        <div>Libellé</div>
                        <div>Date</div>
                        <div style={{ textAlign: 'right' }}>Montant</div>
                      </div>
                      {installments.map((ins, idx) => (
                        <div key={idx} style={{ display: 'grid', gridTemplateColumns: '40px 1fr 120px 100px', padding: '8px 12px', fontSize: '0.85rem', borderBottom: '1px solid var(--admin-border)', alignItems: 'center' }}>
                          <div style={{ color: 'var(--admin-muted)' }}>{idx + 1}</div>
                          <input
                            type="text"
                            value={ins.label}
                            onChange={e => {
                              const newIns = [...installments];
                              newIns[idx].label = e.target.value;
                              setInstallments(newIns);
                            }}
                            style={{ background: 'transparent', border: 'none', color: 'var(--admin-text)', width: '100%', padding: 0 }}
                          />
                          <input
                            type="date"
                            value={ins.due_date ? ins.due_date.split('T')[0] : ''}
                            onChange={e => {
                              const newIns = [...installments];
                              newIns[idx].due_date = e.target.value;
                              setInstallments(newIns);
                            }}
                            style={{ background: 'transparent', border: 'none', color: 'var(--admin-text)', width: '100%', padding: 0 }}
                          />
                          <input
                            type="number"
                            step="0.01"
                            value={ins.amount_due}
                            onChange={e => {
                              const newIns = [...installments];
                              newIns[idx].amount_due = e.target.value === '' ? '' : parseFloat(e.target.value);
                              setInstallments(newIns);
                            }}
                            onWheel={(e) => e.currentTarget.blur()}
                            onKeyDown={(e) => ["ArrowUp", "ArrowDown"].includes(e.key) && e.preventDefault()}
                            className="no-spin"
                            style={{ background: 'transparent', border: 'none', color: 'var(--admin-text)', width: '100%', padding: 0, textAlign: 'right' }}
                          />
                        </div>
                      ))}
                      <div style={{ padding: '8px 12px', textAlign: 'right', fontSize: '0.85rem', fontWeight: 600, background: 'var(--admin-panel-2)' }}>
                        Total: {installments.reduce((sum, i) => sum + (Number(i.amount_due) || 0), 0).toFixed(2)}€
                        {(Math.abs(totals.total - installments.reduce((sum, i) => sum + (Number(i.amount_due) || 0), 0)) > 0.05) && (
                          <span style={{ color: '#ef4444', marginLeft: 8 }}>(Diff: {(totals.total - installments.reduce((sum, i) => sum + (Number(i.amount_due) || 0), 0)).toFixed(2)}€)</span>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="form-actions"><button type="button" onClick={onCancel} className="btn-cancel">Annuler</button><button type="submit" disabled={loading} className="btn-submit">{loading ? 'Enregistrement...' : (invoice ? 'Mettre à jour' : 'Créer la facture')}</button></div>
        </form>

        {showItemEditor && (() => {
          const item = formData.items.find(i => i.id === activeItemId);
          if (!item) return null;
          const cancelItemEditor = () => {
            if (editorIsNewItem && activeItemId) {
              removeItem(activeItemId);
            }
            setShowItemEditor(false);
            setEditorIsNewItem(false);
          };
          return (
            <div className="modal-overlay" onMouseDown={(e) => { if (e.target.classList.contains('modal-overlay')) cancelItemEditor(); }}>
              <div className="modal-content" style={{ maxWidth: 720 }}>
                <div className="modal-header">
                  <h3>Éditer la prestation</h3>
                  <button className="close-btn" type="button" onClick={cancelItemEditor}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" /></svg>
                  </button>
                </div>
                <div className="quick-service-form" style={{ padding: 20 }}>
                  <div className="form-row" style={{ gridTemplateColumns: '200px 1fr' }}>
                    <div className="form-group">
                      <label>Service</label>
                      <select value={item.service_id} onChange={(e) => updateItem(item.id, 'service_id', e.target.value)}>
                        <option value="">Service personnalisé</option>
                        {services.filter(s => s.active).map(s => (
                          <option key={s.id} value={s.id}>{s.name} ({s.base_price}€)</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Description</label>
                      <input type="text" value={item.description} onChange={(e) => updateItem(item.id, 'description', e.target.value)} placeholder="Description de la prestation" />
                    </div>
                  </div>
                  <div className="form-row" style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
                    <div className="form-group">
                      <label>Quantité</label>
                      <input
                        type="number"
                        value={item.quantity}
                        min="0"
                        step="1"
                        onChange={(e) => updateItem(item.id, 'quantity', e.target.value)}
                        onWheel={(e) => e.currentTarget.blur()}
                        onKeyDown={(e) => ["ArrowUp", "ArrowDown"].includes(e.key) && e.preventDefault()}
                        inputMode="numeric"
                        className="no-spin"
                      />
                    </div>
                    <div className="form-group">
                      <label>Prix unitaire (€)</label>
                      <input
                        type="number"
                        value={item.unit_price}
                        min="0"
                        step="0.01"
                        onChange={(e) => updateItem(item.id, 'unit_price', e.target.value)}
                        onWheel={(e) => e.currentTarget.blur()}
                        onKeyDown={(e) => ["ArrowUp", "ArrowDown"].includes(e.key) && e.preventDefault()}
                        inputMode="decimal"
                        className="no-spin"
                      />
                    </div>
                    <div className="form-group">
                      <label>Total</label>
                      <input type="text" value={(Number(item.quantity || 0) * Number(item.unit_price || 0)).toFixed(2) + '€'} readOnly className="readonly" />
                    </div>
                  </div>
                  <div className="modal-actions">
                    <button type="button" className="btn-secondary" onClick={cancelItemEditor}>Annuler</button>
                    <button type="button" className="btn-primary" onClick={() => { setShowItemEditor(false); setEditorIsNewItem(false); }}>Valider</button>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

        <style>{`
          /* Modal styles (shared with Quick* forms) */
          .modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 1001; padding: 20px; }
          .modal-content { background: var(--admin-panel); color: var(--admin-text); border: 1px solid var(--admin-border); border-radius: 12px; width: 100%; max-width: 720px; max-height: 90vh; overflow-y: auto; box-shadow: 0 20px 25px -5px rgba(0,0,0,0.35); }
          .modal-header { display: flex; justify-content: space-between; align-items: center; padding: 20px 20px 0 20px; border-bottom: 1px solid var(--admin-border); margin-bottom: 16px; }
          .modal-header h3 { margin: 0; font-size: 1.2rem; font-weight: 600; color: var(--admin-text); }
          .modal-actions { display: flex; justify-content: flex-end; gap: 12px; padding: 0 20px 20px 20px; border-top: 1px solid var(--admin-border); margin-top: 16px; padding-top: 16px; }
          .btn-secondary { background: var(--admin-panel-2); color: var(--admin-text); border: 1px solid var(--admin-border); padding: 10px 16px; border-radius: 8px; cursor: pointer; }
          .btn-secondary:hover { background: rgba(255,255,255,0.06); }
          .btn-primary { background: var(--admin-accent); color: #fff; border: none; padding: 10px 16px; border-radius: 8px; cursor: pointer; }
          .btn-primary:disabled { opacity: .6; cursor: not-allowed; }

          .invoice-form-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 1000; padding: 20px; }
          .invoice-form-container { background: var(--admin-panel); color: var(--admin-text); border-radius: 12px; width: 100%; max-width: 1000px; max-height: 90vh; overflow-y: auto; overflow-x: hidden; box-shadow: 0 20px 25px -5px rgba(0,0,0,0.35); border: 1px solid var(--admin-border); }
          .invoice-form-header { display: flex; justify-content: space-between; align-items: center; padding: 24px 24px 0 24px; border-bottom: 1px solid var(--admin-border); margin-bottom: 24px; position: sticky; top: 0; background: var(--admin-panel); z-index: 10; }
          .invoice-form-header h2 { margin: 0; font-size: 1.5rem; font-weight: 600; color: var(--admin-text); }
          .close-btn { background: none; border: 1px solid transparent; color: var(--admin-muted); cursor: pointer; padding: 8px; border-radius: 6px; transition: all 0.2s ease; }
          .close-btn:hover { background: rgba(255,255,255,0.06); border-color: var(--admin-border); color: var(--admin-text); }
          .invoice-form { padding: 0 24px 24px 24px; }
          .form-section { margin-bottom: 32px; padding-bottom: 24px; border-bottom: 1px solid var(--admin-border); }
          .form-section:last-of-type { border-bottom: none; }
          .form-section h3 { margin: 0 0 20px 0; font-size: 1.2rem; font-weight: 600; color: var(--admin-text); border-left: 4px solid var(--admin-accent); padding-left: 12px; }
          .section-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; flex-wrap: wrap; gap: 10px; }
          .section-header h3 { margin: 0; }
          .btn-add-item { background: #10b981; color: #fff; border: none; border-radius: 6px; padding: 8px 16px; font-size: 0.9rem; cursor: pointer; transition: all 0.2s ease; display: flex; align-items: center; gap: 6px; }
          .btn-add-item:hover { background: #059669; }
          .section-actions { display: flex; gap: 12px; align-items: center; flex-wrap: wrap; }
          .btn-create-service { background: #8b5cf6; color: #fff; border: none; border-radius: 6px; padding: 8px 16px; cursor: pointer; display: flex; align-items: center; gap: 8px; font-size: 0.875rem; font-weight: 500; transition: all 0.2s ease; }
          .btn-create-service:hover { background: #7c3aed; transform: translateY(-1px); }
          .btn-create-service:active { transform: translateY(0); }
          .btn-add-part { background: #f59e0b; color: #fff; border: none; border-radius: 6px; padding: 8px 16px; cursor: pointer; display: flex; align-items: center; gap: 8px; font-size: 0.875rem; font-weight: 500; transition: all 0.2s ease; }
          .btn-add-part:hover { background: #d97706; transform: translateY(-1px); }
          .btn-add-part:active { transform: translateY(0); }
          .btn-create-part { background: #10b981; color: #fff; border: none; border-radius: 6px; padding: 8px 16px; cursor: pointer; display: flex; align-items: center; gap: 8px; font-size: 0.875rem; font-weight: 500; transition: all 0.2s ease; }
          .btn-create-part:hover { background: #059669; transform: translateY(-1px); }
          .btn-create-part:active { transform: translateY(0); }
          .form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 16px; }
          @media (max-width: 640px) { .form-row { grid-template-columns: 1fr; } }
          .form-group { position: relative; margin-bottom: 16px; }
          .form-group label { display: block; margin-bottom: 6px; font-weight: 500; color: var(--admin-text); font-size: 0.9rem; }
          .form-group input, .form-group select, .form-group textarea { width: 100%; padding: 12px 16px; border: 1px solid var(--admin-border); background: var(--admin-panel-2); color: var(--admin-text); border-radius: 8px; font-size: 0.9rem; transition: all 0.2s ease; box-sizing: border-box; }
          .form-group input:focus, .form-group select:focus, .form-group textarea:focus { outline: none; border-color: var(--admin-accent); box-shadow: 0 0 0 3px rgba(79,70,229,0.20); }
          .form-group input.error, .form-group select.error, .form-group textarea.error { border-color: #dc2626; }
          .form-group input.readonly { background: var(--admin-panel-2); color: var(--admin-muted); }
          .form-group textarea { resize: vertical; min-height: 100px; }
          .error-text { color: #dc2626; font-size: 0.8rem; margin-top: 4px; display: block; }
          .items-table { border: 1px solid var(--admin-border); border-radius: 8px; overflow: hidden; background: transparent; }
          .table-header { display: grid; grid-template-columns: 200px 1fr 80px 120px 100px 60px; background: var(--admin-panel-2); padding: 12px; font-weight: 600; color: var(--admin-text); font-size: 0.9rem; border-bottom: 1px solid var(--admin-border); }
          .table-row { display: grid; grid-template-columns: 200px 1fr 80px 120px 100px 60px; padding: 12px; border-bottom: 1px solid var(--admin-border); align-items: center; gap: 8px; }
          .table-row:last-child { border-bottom: none; }
          .item-service { position: relative; }
          .item-service select, .item-description input, .item-quantity input, .item-price input { width: 100%; max-width: 100%; padding: 8px; border: 1px solid var(--admin-border); background: var(--admin-panel); color: var(--admin-text); border-radius: 4px; font-size: 0.85rem; box-sizing: border-box; }
          .service-search-overlay { position: absolute; inset: 0; z-index: 2; padding: 8px; border: 1px solid var(--admin-border); background: var(--admin-panel); color: var(--admin-text); border-radius: 4px; font-size: 0.85rem; box-shadow: 0 0 0 3px rgba(79,70,229,0.15); }
          .item-total { font-weight: 600; color: var(--admin-accent-2); text-align: right; }
          .btn-remove-item { background: rgba(220,38,38,0.15); color: #dc2626; border: 1px solid rgba(220,38,38,0.3); border-radius: 4px; padding: 6px; cursor: pointer; transition: all 0.2s ease; }
          .btn-remove-item:hover { background: rgba(220,38,38,0.25); }
          .empty-items { text-align: center; padding: 40px; color: var(--admin-muted); }
          .empty-items p { margin: 0 0 16px 0; }
          .totals-section { background: var(--admin-panel-2); padding: 20px; border-radius: 8px; border: 1px solid var(--admin-border); }
          .totals-row { display: flex; justify-content: space-between; margin-bottom: 8px; font-size: 0.9rem; }
          .totals-row.total { font-weight: 600; font-size: 1.1rem; color: var(--admin-text); padding-top: 8px; border-top: 1px solid var(--admin-border); margin-top: 8px; }
          .form-actions { display: flex; gap: 12px; justify-content: flex-end; margin-top: 32px; padding-top: 24px; border-top: 1px solid var(--admin-border); position: sticky; bottom: 0; background: var(--admin-panel); }
          .btn-cancel, .btn-submit { padding: 12px 24px; border-radius: 8px; font-weight: 500; cursor: pointer; transition: all 0.2s ease; border: none; font-size: 0.9rem; }
          .btn-cancel { background: var(--admin-panel-2); color: var(--admin-text); border: 1px solid var(--admin-border); }
          .btn-cancel:hover { background: rgba(255,255,255,0.06); }
          .btn-submit { background: var(--admin-accent); color: #fff; }
          .btn-submit:hover:not(:disabled) { filter: brightness(0.95); }
          .btn-submit:disabled { opacity: 0.6; cursor: not-allowed; }
          .select-with-add { display: flex; gap: 8px; align-items: flex-start; }
          .select-with-add select { flex: 1; }
          .add-btn { background: #10b981; color: #fff; border: none; border-radius: 6px; padding: 10px; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: all 0.2s ease; flex-shrink: 0; height: 42px; width: 42px; }
          .add-btn:hover { background: #059669; transform: scale(1.05); }
          .add-btn:active { transform: scale(0.95); }
          .add-btn:disabled { background: #9ca3af; cursor: not-allowed; transform: none; }
          .add-btn:disabled:hover { background: #9ca3af; transform: none; }
          .info-text { color: var(--admin-muted); font-size: 0.8rem; margin-top: 4px; font-style: italic; }

          /* QuickVehicle form spacing identical to QuoteForm */
          .quick-vehicle-modal { max-width: 800px; width: 90vw; }
          .quick-vehicle-form { padding: 24px; }
          .quick-vehicle-form .form-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 20px; margin-bottom: 24px; }
          .quick-vehicle-form .form-actions { display: flex; justify-content: flex-end; gap: 12px; padding-top: 20px; border-top: 1px solid var(--admin-border); margin-top: 24px; }
          .quick-vehicle-form .form-group { margin-bottom: 4px; }
          .quick-vehicle-form label { display: block; margin-bottom: 6px; font-weight: 500; color: var(--admin-text); font-size: 0.875rem; }
          .quick-vehicle-form input, .quick-vehicle-form select, .quick-vehicle-form textarea { width: 100%; padding: 10px 12px; border: 1px solid var(--admin-border); background: var(--admin-panel-2); color: var(--admin-text); border-radius: 6px; font-size: 0.875rem; box-sizing: border-box; }
          .quick-vehicle-form input:focus, .quick-vehicle-form select:focus, .quick-vehicle-form textarea:focus { outline: none; border-color: var(--admin-accent); box-shadow: 0 0 0 3px rgba(79,70,229,0.20); }

          /* Mirror QuoteForm mobile behavior for items */
          @media (max-width: 768px) {
            .table-header, .table-row { grid-template-columns: 1fr; gap: 8px; }
            .table-header { display: none; }
            .table-row { display: flex; flex-direction: column; padding: 16px; width: 100%; }
            .table-row > * { width: 100%; max-width: 100%; }
            .item-total { text-align: left; }
          }

          /* Responsive: prevent horizontal overflow of action buttons */
            .section-actions { width: 100%; }
            .section-actions > button { flex: 1 1 220px; min-width: 0; }
          }

          /* Hide spin buttons */
          input[type=number].no-spin::-webkit-inner-spin-button, 
          input[type=number].no-spin::-webkit-outer-spin-button { 
            -webkit-appearance: none; 
            margin: 0; 
          }
          input[type=number].no-spin {
            -moz-appearance: textfield;
          }
        `}</style>
      </div>

      {showQuickCustomerForm && (<QuickCustomerForm onSubmit={(d) => { setCreatingCustomer(true); return noopWrap(async (data) => { const res = await onCreateCustomer(data); setFormData(prev => ({ ...prev, customer_id: res.id })); updateAvailableVehicles(res.id); updateAvailableQuotes(res.id); setShowQuickCustomerForm(false); return res; }, d).finally(() => setCreatingCustomer(false)); }} onCancel={() => setShowQuickCustomerForm(false)} loading={creatingCustomer} />)}
      {showQuickVehicleForm && (<QuickVehicleForm customerId={formData.customer_id} onSubmit={(d) => { setCreatingVehicle(true); return noopWrap(async (data) => { const res = await onCreateVehicle({ ...data, client: formData.customer_id, customer_id: formData.customer_id, customer: formData.customer_id }); setFormData(prev => ({ ...prev, vehicle_id: res.id })); updateAvailableVehicles(formData.customer_id); setShowQuickVehicleForm(false); return res; }, d).finally(() => setCreatingVehicle(false)); }} onCancel={() => setShowQuickVehicleForm(false)} loading={creatingVehicle} />)}
      {showQuickServiceForm && (<QuickServiceForm onSubmit={(d) => { setCreatingService(true); return noopWrap(async (data) => { const res = await onCreateService(data); setShowQuickServiceForm(false); return res; }, d).finally(() => setCreatingService(false)); }} onCancel={() => setShowQuickServiceForm(false)} loading={creatingService} />)}
      {showPartSelector && (<QuickPartSelector parts={parts || []} onSubmit={handleAddPart} onCancel={() => setShowPartSelector(false)} loading={false} />)}
      {showQuickPartForm && (<PartForm onSave={(d) => { setCreatingPart(true); return noopWrap(async (data) => { const res = await onCreatePart(data); setShowQuickPartForm(false); return res; }, d).finally(() => setCreatingPart(false)); }} onCancel={() => setShowQuickPartForm(false)} loading={creatingPart} />)}
    </div>
  );
};

export default InvoiceForm;
